import API_CONFIG from '../../config/api.config';

/**
 * Creates a full API URL by combining the base URL with the endpoint
 * @param {string} endpoint - The API endpoint path
 * @returns {string} The complete API URL
 */
export const createApiUrl = (endpoint) => {
  return `${API_CONFIG.BASE_URL}${endpoint}`;
};

/**
 * Handles the API response and converts it to JSON
 * Also checks for error responses and throws appropriate errors
 * @param {Response} response - The fetch Response object
 * @returns {Promise<any>} The parsed JSON response
 * @throws {Error} If the response is not ok
 */
export const handleApiResponse = async (response) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(
      errorData?.message || 
      `HTTP error! status: ${response.status} ${response.statusText}`
    );
  }
  return response.json();
};

const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Enhanced fetch with retry logic, timeout control, and better CORS handling
 * @param {string} url - The URL to fetch
 * @param {object} options - Fetch options
 * @param {number} maxRetries - Maximum number of retries
 * @param {number} timeout - Timeout in milliseconds
 * @returns {Promise<Response>} The fetch response
 */
export async function fetchWithRetry(url, options, maxRetries = 1, timeout = 30000) {
  let lastError;
  
  // Add common headers for better CORS handling if not already provided
  const enhancedOptions = {
    ...options,
    headers: {
      ...options.headers,
      'Cache-Control': options.headers['Cache-Control'] || 'max-age=3600',
      'Accept': options.headers['Accept'] || 'application/json',
    },
    // Always include credentials and mode
    credentials: options.credentials || 'include',
    mode: options.mode || 'cors',
  };
  
  for (let i = 0; i < maxRetries; i++) {
    try {
      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      
      try {
        // Add signal to options
        const requestOptions = {
          ...enhancedOptions,
          signal: controller.signal,
        };
        
        const response = await fetch(url, requestOptions);
        
        if (!response.ok) {
          // Client errors: do not retry, return response immediately
          if (response.status >= 400 && response.status < 500) {
            return response;
          }
          // For CORS errors, add more detailed logging
          if (response.status === 0 || response.type === 'opaque' || response.type === 'error') {
            console.error('Possible CORS error detected', { url, requestOptions });
            throw new Error(`CORS error or network failure detected`);
          }
          // Server errors: retry when possible, otherwise return the response
          // so upper layers can parse the backend detail payload.
          if (i < maxRetries - 1) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response;
        }
        
        return response;
      } finally {
        clearTimeout(timeoutId);
      }
    } catch (error) {
      console.warn(`Fetch attempt ${i + 1} failed:`, error.message);
      lastError = error;
      
      if (i === maxRetries - 1) break;
      
      // Exponential backoff with jitter to prevent request thundering herd
      const backoffTime = Math.pow(2, i) * 1000 + Math.random() * 1000;
      console.log(`Retrying in ${backoffTime}ms...`);
      await wait(backoffTime);
    }
  }
  
  throw lastError;
} 

/**
 * Ca fetchWithRetry, dar reîncearcă o dată după reîmprospătarea tokenului dacă
 * serverul răspunde 401.
 *
 * `fetchWithRetry` returnează erorile 4xx direct, fără reîncercare — ceea ce e
 * corect pentru 400 sau 404, dar nu și pentru 401: acolo cererea e recuperabilă
 * cu un token nou. Serviciile care apelează prin `apiClient` au deja acest
 * comportament; cele care cheamă fetch direct, nu.
 *
 * Contează pe terminalul public: tokenul lui trăiește minute, nu 15 minute ca pe
 * un laptop, deci un 401 din cauza expirării e mult mai probabil să prindă exact
 * o cerere de căutare — și apărea omului ca „Failed to fetch SKUs", nu ca o
 * reautentificare tăcută.
 *
 * @param {string} url
 * @param {object} options - trebuie să conțină headers.Authorization
 * @param {number} maxRetries
 * @param {number} timeout
 * @param {Function} refreshToken - reîmprospătează și întoarce noul token sau null
 */
export async function fetchWithAuthRetry(url, options, maxRetries, timeout, refreshToken) {
  const response = await fetchWithRetry(url, options, maxRetries, timeout);
  if (response.status !== 401) return response;

  const newToken = await refreshToken();
  if (!newToken) return response;

  return fetchWithRetry(
    url,
    { ...options, headers: { ...options.headers, Authorization: `Bearer ${newToken}` } },
    maxRetries,
    timeout,
  );
}
